/*
Copied and adapted from Sideway's address library (https://github.com/sideway/address), specifically
domain validation (https://github.com/sideway/address/blob/master/lib/domain.js)

Copyright (c) 2019-2020, Sideway, Inc. and Project contributors
All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
 * Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
 * Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
 * The names of any contributors may not be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDERS AND CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

/* eslint-disable no-useless-escape */
/* eslint-disable no-control-regex */
const internals = {
  nonAsciiRx: /[^\x00-\x7f]/,
  domainControlRx: /[\x00-\x20@\:\/]/, // Control + space + separators
  tldSegmentRx: /^[a-zA-Z](?:[a-zA-Z0-9\-]*[a-zA-Z0-9])?$/,
  domainSegmentRx: /^[a-zA-Z0-9](?:[a-zA-Z0-9\-]*[a-zA-Z0-9])?$/,
};

export default function validateDomain(domain, options = {}, t) {
  if (!domain) {
    return null;
  }

  // validate value as subdomain of current environment
  if (options.isSubdomain) {
    domain = `${domain}.${window.location.hostname}`; // hostname to exclude port
  }

  if (domain.length > 256) {
    return typeof t === 'function'
      ? t('Domain must not be longer than 256 characters')
      : 'Domain must not be longer than 256 characters';
  }

  // TODO How do we test this?
  const ascii = !internals.nonAsciiRx.test(domain);
  if (!ascii) {
    domain = domain.normalize('NFC');
  }

  if (internals.domainControlRx.test(domain)) {
    return typeof t === 'function'
      ? t('Domain contains invalid character(s)')
      : 'Domain contains invalid character(s)';
  }

  domain = internals.punycode(domain);

  // https://tools.ietf.org/html/rfc1035 section 2.3.1

  const segments = domain.split('.');
  const minSegments =
    Number.isInteger(options.minDomainSegments) && options.minDomainSegments;

  if (minSegments >= 2 && !domain.includes('.')) {
    return typeof t === 'function'
      ? t('Domain Names require a “.”')
      : 'Domain Names require a “.”';
  }

  if (segments.length < minSegments) {
    return typeof t === 'function'
      ? `${t(
          'Domain lacks the minimum required number of segments'
        )} (${minSegments})`
      : `Domain lacks the minimum required number of segments (${minSegments})`;
  }

  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];

    if (!segment.length) {
      return typeof t === 'function'
        ? t('Domain contains an empty dot-separated segment')
        : 'Domain contains an empty dot-separated segment';
    }

    if (segment.length > 63) {
      return typeof t === 'function'
        ? t(
            'Domain contains dot-separated segment that is too long (> 63 characters)'
          )
        : 'Domain contains dot-separated segment that is too long (> 63 characters)';
    }

    if (i < segments.length - 1) {
      if (!internals.domainSegmentRx.test(segment)) {
        return typeof t === 'function'
          ? t('Domain contains invalid character(s)')
          : 'Domain contains invalid character(s)';
      }
    } else if (!internals.tldSegmentRx.test(segment)) {
      return typeof t === 'function'
        ? t('Domain contains invalid tld character')
        : 'Domain contains invalid tld character';
    }
  }

  return null;
}

internals.punycode = (domain) => {
  try {
    return new internals.URL(`http://${domain}`).host;
  } catch (err) {
    return domain;
  }
};
